import { cx } from "../ui/primitives";
import type { RegionMeta } from "./ViewSurface";
import { maskIf } from "../../lib/replayMask";

/**
 * The label cluster a region tile wears: the name badge (sync-group tinted) and the
 * feed/pacing pills under it. One definition for every surface that labels a region —
 * the editor's canvas tiles and the screen's hover chrome — so the same region can
 * never read differently in the two places it is named.
 *
 * `pointer-events-none` on the wrapper: labels describe the tile, they are not controls,
 * and on the wall a label that ate a click would eat the click that opens the popup.
 */
export function RegionTileLabels({
  name,
  index,
  meta,
  selected = false,
  syncTint,
}: {
  name: string | undefined;
  /** Position in the view's region list, for the "Region N" fallback. */
  index: number;
  meta: RegionMeta | null;
  /** The editor's selection accent; the wall has no selection and leaves it off. */
  selected?: boolean;
  /** Sync-group colour for the name badge: every region in one sync group wears the
   *  same tint, so linked tiles read as a set at a glance. Absent = default badge. */
  syncTint?: string | null;
}) {
  return (
    <div className="pointer-events-none relative z-10 m-1 flex max-w-[calc(100%-8px)] flex-col items-start gap-0.5">
      <span
        className={cx(
          "max-w-full truncate rounded px-1.5 py-0.5 text-[10px] font-medium",
          selected
            ? "bg-[var(--color-accent)] text-white"
            : syncTint
              ? "text-white"
              : "bg-[var(--color-ink)]/80 text-zinc-300",
        )}
        // The selection accent outranks the group tint: what you are editing
        // matters more than what it is linked to, for the moment you edit it.
        style={!selected && syncTint ? { backgroundColor: syncTint } : undefined}
        // A region's name is the operator's own words; the "Region N" fallback is ours.
        {...maskIf(Boolean(name?.trim()))}
      >
        {name?.trim() || `Region ${index + 1}`}
      </span>
      {meta ? (
        <>
          <span className="max-w-full truncate rounded bg-[var(--color-ink)]/80 px-1.5 py-0.5 text-[10px] text-zinc-200">
            {meta.feed}
          </span>
          {meta.timer ? (
            <span className="max-w-full truncate rounded bg-[var(--color-ink)]/80 px-1.5 py-0.5 text-[10px] text-zinc-500">
              {meta.timer}
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
